import { useState } from 'react';
import { ActionMenu, ActionList, TextInput } from '@primer/react';
import { PlusIcon, SearchIcon } from '@primer/octicons-react';
import styled from 'styled-components';
import { AddButton, ListToken } from './shared/tokens';
import { SectionContainer, ItemsContainer, ItemsList } from './shared/section-styles';
import { SectionTitleWithGuidance } from './shared/section-guidance';
import { AbsentRawMaterialType } from '@upright/common/model/input/companies/products/ipr';
import { ATTRIBUTE_GUIDANCE } from '@upright/common/model/input/companies/products/ipr/attribute-guidance';

export interface AbsentRawMaterialItem {
  id: AbsentRawMaterialType;
  title: string;
}

interface AbsentRawMaterialsProps {
  absentMaterials: AbsentRawMaterialItem[];
  onAbsentMaterialsChange: (absentMaterials: AbsentRawMaterialItem[]) => void;
}

const ABSENT_RAW_MATERIALS: AbsentRawMaterialItem[] = Object.values(AbsentRawMaterialType).map(value => ({
  id: value,
  title: value,
}));

export function AbsentRawMaterials({ absentMaterials, onAbsentMaterialsChange }: AbsentRawMaterialsProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const availableMaterials = ABSENT_RAW_MATERIALS.filter(
    material =>
      !absentMaterials.some(m => m.id === material.id) &&
      material.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const addMaterial = (material: AbsentRawMaterialItem) => {
    onAbsentMaterialsChange([...absentMaterials, material]);
    setIsMenuOpen(false);
    setSearchTerm('');
  };

  const handleMenuOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      setSearchTerm('');
    }
  };

  const removeMaterial = (material: AbsentRawMaterialItem) => {
    onAbsentMaterialsChange(absentMaterials.filter(m => m.id !== material.id));
  };

  return (
    <SectionContainer>
      <SectionTitleWithGuidance title="Absent raw materials" guidance={ATTRIBUTE_GUIDANCE.absentRawMaterials} />

      <ItemsContainer>
        <ItemsList>
          {absentMaterials.map(material => (
            <ListToken
              key={material.id}
              size="medium"
              onRemove={() => {
                removeMaterial(material);
              }}
              text={material.title}
            />
          ))}
          <ActionMenu open={isMenuOpen} onOpenChange={handleMenuOpenChange}>
            <ActionMenu.Anchor>
              <AddButton size="medium" leadingVisual={PlusIcon} text="Add material absence" />
            </ActionMenu.Anchor>

            <ActionMenu.Overlay width="large">
              <SearchContainer>
                <TextInput
                  placeholder="Search materials..."
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                  }}
                  leadingVisual={SearchIcon}
                  size="small"
                />
              </SearchContainer>
              <ScrollableActionListContainer>
                <ActionList>
                  <ActionList.Group title="Select a material absence...">
                    {availableMaterials.length > 0 ? (
                      availableMaterials.map(material => (
                        <ActionList.Item
                          key={material.id}
                          onSelect={() => {
                            addMaterial(material);
                          }}
                        >
                          {material.title}
                        </ActionList.Item>
                      ))
                    ) : (
                      <ActionList.Item disabled>
                        {searchTerm ? 'No matching materials found' : 'All material absences added'}
                      </ActionList.Item>
                    )}
                  </ActionList.Group>
                </ActionList>
              </ScrollableActionListContainer>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ItemsList>
      </ItemsContainer>
    </SectionContainer>
  );
}

const SearchContainer = styled.div`
  padding: var(--space__sm);
  border-bottom: 1px solid var(--borderColor-default);
`;

const ScrollableActionListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;
